import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertCircle,
  AlertTriangle,
  Archive,
  ArchiveRestore,
  Braces,
  Check,
  Copy,
  Download,
  Loader2,
  Settings2,
  ShieldCheck,
  Sparkles,
  Trash2,
  Wand2,
} from 'lucide-react';
import { META_LABEL, FormSection, SaveResetActions } from '../playbooks/editorBits';
import { downloadSchemaExport } from '../serviceKeysApi';
import { fixSchemaDraft, generateSchemaItemFields, previewMatchingBatchRun } from '../../structured-data/structuredDataApi';
import { schemaToDraft, slugifyKey } from './useDataSchemas';
import { validateDefinition, semanticWarnings, constrainedOutputStatus } from './schemaMetaValidator';
import SchemaJsonEditor from './SchemaJsonEditor';
import SchemaPreview from './SchemaPreview';
import DraftWithAiPane from './DraftWithAiPane';
import AiFixPane from './AiFixPane';
import TestRunPane from './TestRunPane';
import BatchRunPane from './BatchRunPane';
import SchemaAutomationPanel from './SchemaAutomationPanel';

const isContainer = (type) => type === 'array' || type === 'object';

// `"a"`, `"a" and "b"`, `"a", "b" and 2 more` — for naming offending fields in
// an advisory without letting a 40-key schema print a paragraph of keys.
function listKeys(keys) {
  const shown = keys.slice(0, 3).map((k) => `"${k}"`);
  if (keys.length > shown.length) shown.push(`${keys.length - shown.length} more`);
  if (shown.length === 1) return shown[0];
  return `${shown.slice(0, -1).join(', ')} and ${shown[shown.length - 1]}`;
}

// The canonical portable definition — what the JSON editor shows and the
// Download button writes. Defaults are omitted so the JSON stays clean.
export function draftToCanonicalJson(draft) {
  const fields = (draft.fields || []).map((f) => ({
    key: f.key,
    label: f.label,
    type: f.type,
    description: f.description,
    ...(f.type === 'enum' ? { options: f.options || [] } : {}),
    ...(isContainer(f.type) && Array.isArray(f.itemFields) && f.itemFields.length
      ? {
        itemFields: f.itemFields.map((sf) => ({
          key: sf.key,
          label: sf.label,
          type: sf.type,
          description: sf.description,
          ...(sf.type === 'enum' ? { options: sf.options || [] } : {}),
        })),
      }
      : {}),
  }));
  return {
    name: draft.name,
    description: draft.description || undefined,
    targetTypes: draft.targetTypes,
    targetCategories: draft.targetCategories,
    // Always emitted, same reason as includeRelatedSources below: absent means
    // "keep stored" on the server, so Download/re-upload must be explicit.
    includeDrafts: !!draft.includeDrafts,
    includeTerminated: !!draft.includeTerminated,
    fields,
    promptPreamble: draft.promptPreamble || undefined,
    model: draft.model || undefined,
    // Always emitted (unlike other defaults): the server treats an absent key
    // as "keep the stored value" on update, so only an explicit boolean makes
    // Download/re-upload faithful (doc-86).
    includeRelatedSources: !!draft.includeRelatedSources,
    active: draft.active,
  };
}

const prettyCanonical = (draft) => JSON.stringify(draftToCanonicalJson(draft), null, 2);

function parseJsonText(text) {
  try {
    const parsed = JSON.parse(text);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return { parsed: null, error: { message: 'The top level must be a JSON object.', pos: 0 } };
    }
    return { parsed, error: null };
  } catch (err) {
    const m = String(err.message).match(/position (\d+)/);
    return { parsed: null, error: { message: err.message, pos: m ? Number(m[1]) : 0 } };
  }
}

function TargetChecklist({ title, options, value = [], onChange, disabled }) {
  const selected = new Set(value);
  const toggle = (key) => {
    const next = new Set(selected);
    if (next.has(key)) next.delete(key); else next.add(key);
    onChange(options.filter((o) => next.has(o)));
  };
  return (
    <div>
      <div style={{ ...META_LABEL, marginBottom: 6 }}>{title}</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(210px, 1fr))', gap: '1px 12px' }}>
        {options.map((o) => (
          <label key={o} className="pb-check" title={o}>
            <input type="checkbox" checked={selected.has(o)} onChange={() => toggle(o)} disabled={disabled} />
            <span>{o}</span>
          </label>
        ))}
      </div>
    </div>
  );
}

const VERSION_HINT = 'Saving a change to fields or prompts bumps the version — outputs from older versions stay readable, labeled stale.';
const STATE_HINT = 'Archive, restore, and delete live in Settings; the JSON "active" key also controls this on save.';

// The three jobs, in frequency order: understand/edit the definition, adjust
// settings, exercise it (doc-105 design spec §2).
const TABS = [
  { key: 'definition', label: 'Definition', icon: Braces },
  { key: 'settings', label: 'Settings', icon: Settings2 },
  { key: 'test', label: 'Test & run', icon: Sparkles },
];

const PANEL_PADDING = { padding: '20px 24px 24px' };

// Admin > Data Schemas editor (doc-105 IA rethink over the doc-74 JSON-first
// editor): ONE card per schema — a sticky header (name, chips, Save/Reset,
// one-line meta summary, job-mapped tabs) over three tab panels:
//   Definition — the edit/validate loop (AI panes, one full-width pane with a
//                JSON | Preview view toggle — doc-108)
//   Settings   — name/description, targeting, model + preamble, archive/delete
//   Test & run — draft dry-run, batch run + export of the SAVED version
// Panels stay MOUNTED (hidden attr) so test/batch state survives tab
// switches; per-schema reset comes from the page-level key={selectedId}.
//
// Sync rules (doc-74 spec §2.5) — the draft is the single source of truth:
//   form edit  -> patch draft -> regenerate editor text (pretty-printed)
//   editor edit -> parse -> patch draft (hand formatting is preserved)
//   while the text doesn't PARSE, the scalar inputs disable (a form edit
//   would clobber the unparsed hand edits) and the preview dims to last-valid
export default function DataSchemaEditor({
  draft,
  base,           // saved schema (null for new drafts)
  isNew,
  isDirty,
  models,
  typeOptions,
  categoryOptions,
  metaSchema,     // GET /api/schemas/meta-schema response (null while loading)
  // Run, export, and automation permissions are independent.
  canRunBatches = false,
  canExport = false,
  canManageAutomation = false,
  onSaveAutomation,
  saving,
  onPatch,        // (partial draft) => void
  onSave,
  onReset,
  onArchive,      // () => void — page opens the confirm modal
  onRestore,      // () => void — one-click, no confirm
  onDelete,       // () => void — page opens the guarded confirm modal
}) {
  const [jsonText, setJsonText] = useState(() => prettyCanonical(draft));
  const [tab, setTab] = useState('definition');
  // Definition view toggle (doc-108): ONE full-width pane showing the JSON
  // editor or the readable preview, never both. Not persisted — resets to
  // JSON on schema switch via the page-level key={selectedId} remount. Both
  // views stay mounted (hidden attr) so CM undo history and the preview's
  // scroll position survive a round trip.
  const [view, setView] = useState('json');
  // New schemas open with the Draft pane expanded — the novice path is the
  // default path; the expert just collapses it and types.
  const [aiOpen, setAiOpen] = useState(isNew);
  const [aiNotice, setAiNotice] = useState(false);
  const [pendingAiDraft, setPendingAiDraft] = useState(null);
  const [backfillBusyKey, setBackfillBusyKey] = useState(null);
  const [backfillError, setBackfillError] = useState(null);
  const [copied, setCopied] = useState(false);

  // "Fix with AI" (doc-87). pendingFix pins the buffer text the fix was
  // computed against — any buffer change (typing, form edit, reset) makes the
  // diff stale, so the pane simply stops rendering; the buffer is never
  // touched without an explicit Apply.
  const [fixBusy, setFixBusy] = useState(false);
  const [fixError, setFixError] = useState(null);
  const [pendingFix, setPendingFix] = useState(null); // { requestText, fixedText }

  const [exportVerifiedOnly, setExportVerifiedOnly] = useState(false);
  const [exporting, setExporting] = useState('');       // '' | 'csv' | 'json'
  const [exportError, setExportError] = useState('');

  // Batch-run preview, lifted out of BatchRunPane (doc-105): the header's
  // "N matching" fact and the pane's run button share one request per saved
  // schema. Any save can move the counts — a version bump changes what
  // "fresh" means, and a targeting-only save (no version bump) changes what
  // matches — so watch updatedAt, not just version. (A rail switch remounts
  // this whole editor via the page-level key.)
  const [batchPreview, setBatchPreview] = useState(null);
  const [batchPreviewError, setBatchPreviewError] = useState(null);
  const savedAt = `${base?.version ?? ''}:${base?.updatedAt ?? ''}`;
  useEffect(() => {
    setBatchPreview(null);
    setBatchPreviewError(null);
  }, [savedAt]);
  const baseId = base?.id || null;
  const baseActive = !!base?.active;
  useEffect(() => {
    if (!baseId || !baseActive || !canRunBatches || batchPreview !== null) return undefined;
    let cancelled = false;
    setBatchPreviewError(null);
    previewMatchingBatchRun(baseId)
      .then((res) => { if (!cancelled) setBatchPreview(res); })
      .catch((err) => { if (!cancelled) setBatchPreviewError(err.message); });
    return () => { cancelled = true; };
  }, [baseId, baseActive, canRunBatches, batchPreview]);

  const editorRef = useRef(null);
  // Pretty canonical of the draft the editor text currently reflects — the
  // guard that stops the two sync effects from ping-ponging.
  const lastSyncedCanonical = useRef(prettyCanonical(draft));

  const { parsed, error: parseError } = useMemo(() => parseJsonText(jsonText), [jsonText]);
  const errors = useMemo(
    () => (parseError ? [] : validateDefinition(parsed, metaSchema)),
    [parsed, parseError, metaSchema]
  );
  const warnings = useMemo(
    () => (parseError ? [] : semanticWarnings(parsed)),
    [parsed, parseError]
  );
  // Whether this definition will actually run with constrained structured
  // outputs (doc-320). Deliberately NOT folded into `warnings`: those feed the
  // gutter markers and the Fix-with-AI findings list, and "you are over the
  // provider's union cap" has no minimal JSON edit — an AI asked to fix it
  // would either split the schema (destructive) or convert typed fields to
  // Choice fields (lossy). It is a statement about the run, shown to a human.
  const constrained = useMemo(
    () => (parseError ? null : constrainedOutputStatus(parsed, metaSchema)),
    [parsed, parseError, metaSchema]
  );

  // Editor text -> draft. Any parseable text patches the draft (meta-schema
  // errors block SAVE, not the mirror — otherwise the form inputs would lock
  // up over a fixable key typo).
  useEffect(() => {
    if (parseError || !parsed) return;
    const nextDraft = schemaToDraft(parsed);
    const nextCanonical = prettyCanonical(nextDraft);
    lastSyncedCanonical.current = nextCanonical;
    if (nextCanonical !== prettyCanonical(draft)) onPatch(nextDraft);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jsonText]);

  // Draft -> editor text (form edits, reset). Skipped when the draft change
  // originated in the editor, so hand formatting survives typing.
  useEffect(() => {
    const canonical = prettyCanonical(draft);
    if (canonical !== lastSyncedCanonical.current) {
      lastSyncedCanonical.current = canonical;
      setJsonText(canonical);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft]);

  // Push inline markers (debounced so keystrokes don't flicker the gutter).
  useEffect(() => {
    const t = setTimeout(() => {
      const issues = parseError
        ? [{ pos: parseError.pos, severity: 'error', message: parseError.message }]
        : [
          ...errors.map((e) => ({ path: e.path, severity: 'error', message: e.message })),
          ...warnings.map((w) => ({ path: w.path, severity: 'warning', message: w.message })),
        ];
      editorRef.current?.applyIssues(issues);
    }, 250);
    return () => clearTimeout(t);
  }, [parseError, errors, warnings]);

  const scalarsDisabled = !!parseError;
  // A stored model id the server no longer offers (doc-280 retired two). The
  // options list is server-owned, so "legacy" is defined by absence from it —
  // nothing about the retired ids is hardcoded here.
  const legacyModel = draft.model && !models.some((m) => m.value === draft.model)
    ? draft.model
    : null;
  const errorCount = (parseError ? 1 : 0) + errors.length;
  let saveDisabledReason = null;
  if (parseError) saveDisabledReason = 'Fix the JSON syntax error in the definition';
  else if (errors.length > 0) saveDisabledReason = `Fix the ${errors.length} error${errors.length === 1 ? '' : 's'} in the definition`;

  const fieldCount = Array.isArray(parsed?.fields) ? parsed.fields.length : 0;
  const itemFieldCount = Array.isArray(parsed?.fields)
    ? parsed.fields.filter((f) => f && Array.isArray(f.itemFields) && f.itemFields.length).length
    : 0;

  // Everything the editor currently flags, in the order shown — a parse error
  // is the only finding while the text doesn't parse (the validators can't
  // run), otherwise meta-schema errors then advisory warnings.
  const activeFindings = useMemo(() => (
    parseError
      ? [{ severity: 'error', label: '', message: parseError.message }]
      : [
        ...errors.map((e) => ({ severity: 'error', label: e.label || '', message: e.message })),
        ...warnings.map((w) => ({ severity: 'warning', label: '', message: w.message })),
      ]
  ), [parseError, errors, warnings]);

  const requestFix = async () => {
    const requestText = jsonText;
    setFixBusy(true);
    setFixError(null);
    setPendingFix(null);
    try {
      const res = await fixSchemaDraft({ jsonText: requestText, findings: activeFindings });
      setPendingFix({ requestText, fixedText: res.fixedText });
    } catch (err) {
      setFixError(err.message);
    } finally {
      setFixBusy(false);
    }
  };

  const applyAiDraft = (schema) => {
    const nextText = prettyCanonical(schemaToDraft(schema));
    setJsonText(nextText);
    setAiOpen(false);
    setPendingAiDraft(null);
    setAiNotice(true);
    // The readable projection is the review surface for a fresh AI draft —
    // this is the novice's moment, not the JSON-literate loop. (Applying a
    // Fix-with-AI diff stays in JSON: that's a JSON-level operation.)
    setView('preview');
  };

  // Error rows are visible in both views, but the marker lives in the editor —
  // a scroll into a hidden editor is a no-op, so flip to JSON first and defer
  // the jump a frame for CodeMirror to re-measure.
  const jumpToIssue = (issue) => {
    if (view !== 'json') {
      setView('json');
      requestAnimationFrame(() => editorRef.current?.scrollToIssue(issue));
    } else {
      editorRef.current?.scrollToIssue(issue);
    }
  };

  const onAiDraft = (schema) => {
    // Anything beyond an empty starter definition gets a confirm before the
    // editor content is overwritten.
    const nonStarter = fieldCount > 0 || !!(parsed?.name && String(parsed.name).trim());
    if (nonStarter) setPendingAiDraft(schema);
    else applyAiDraft(schema);
  };

  const backfillItemFields = async (warning) => {
    if (!parsed || !Array.isArray(parsed.fields)) return;
    const field = parsed.fields[warning.fieldIndex];
    if (!field) return;
    setBackfillBusyKey(warning.fieldKey || String(warning.fieldIndex));
    setBackfillError(null);
    try {
      const res = await generateSchemaItemFields({
        field,
        schema: { name: parsed.name, description: parsed.description, promptPreamble: parsed.promptPreamble },
      });
      const next = {
        ...parsed,
        fields: parsed.fields.map((f, i) => (i === warning.fieldIndex ? { ...f, itemFields: res.itemFields } : f)),
      };
      setJsonText(JSON.stringify(next, null, 2));
    } catch (err) {
      setBackfillError(err.message);
    } finally {
      setBackfillBusyKey(null);
    }
  };

  const downloadJson = () => {
    const slug = slugifyKey(draft.name || 'schema') || 'schema';
    const blob = new Blob([jsonText], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${slug}.v${base?.version || 1}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const copyJson = async () => {
    try {
      await navigator.clipboard.writeText(jsonText);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (_) { /* clipboard unavailable — the editor is selectable */ }
  };

  // Header meta line (spec §3.3 row 2) — mid-dot–joined facts, each omitted
  // when unknown. Counts come from the DRAFT (last-valid while the text
  // doesn't parse), matching from the shared batch preview once resolved.
  const draftFields = Array.isArray(draft.fields) ? draft.fields : [];
  const draftFieldCount = draftFields.length;
  const draftItemFieldCount = draftFields.filter((f) => f && Array.isArray(f.itemFields) && f.itemFields.length).length;
  const targetTypes = draft.targetTypes || [];
  const targetCategories = draft.targetCategories || [];
  const targetingParts = [];
  if (targetTypes.length) targetingParts.push(`${targetTypes.length} type${targetTypes.length === 1 ? '' : 's'}`);
  if (targetCategories.length) targetingParts.push(`${targetCategories.length} categor${targetCategories.length === 1 ? 'y' : 'ies'}`);
  const targetingTitle = targetingParts.length ? [...targetTypes, ...targetCategories].join(', ') : undefined;
  // Status targeting reads as an EXCLUSION (doc-284): the checkboxes are
  // opt-in, so unchecked means those contracts are out of scope — the opposite
  // of the type/category lists, where unchecked means "all". Say so out loud
  // in both summaries rather than leaving "targets all contracts" to lie.
  const statusExclusions = [];
  if (!draft.includeDrafts) statusExclusions.push('draft');
  if (!draft.includeTerminated) statusExclusions.push('terminated');
  const exclusionFact = statusExclusions.length ? `excludes ${statusExclusions.join(' + ')}` : null;
  const appliesToSummary = `${targetingParts.length ? `Targets ${targetingParts.join(' · ')}` : 'Targets all contracts'}`
    + `${statusExclusions.length ? `, excluding ${statusExclusions.join(' and ')} contracts` : ''}.`;
  const outputCount = base?.outputCount;
  const metaFacts = [
    { text: `${draftFieldCount} field${draftFieldCount === 1 ? '' : 's'}` },
    ...(draftItemFieldCount > 0 ? [{ text: `${draftItemFieldCount} with item fields` }] : []),
    { text: targetingParts.length ? targetingParts.join(' · ') : 'targets all contracts', title: targetingTitle },
    ...(exclusionFact ? [{ text: exclusionFact }] : []),
    ...(batchPreview ? [{ text: `${batchPreview.matching} matching` }] : []),
    ...(!isNew && base && typeof outputCount === 'number'
      ? [{ text: `${outputCount} output${outputCount === 1 ? '' : 's'}` }]
      : []),
    ...(!isNew && base?.updatedAt
      ? [{ text: `Schema last edited ${new Date(base.updatedAt).toLocaleDateString()}${base.updatedByName ? ` by ${base.updatedByName}` : ''}` }]
      : []),
  ];

  return (
    <div className="card" style={{ minWidth: 0 }}>
      {/* ---- Sticky schema header: identity, state, actions, tabs (§3.3) ---- */}
      <div className="sde-header" style={{ padding: '16px 24px 0' }}>
        <div className="row" style={{ gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          <span
            style={{
              fontSize: 15,
              fontWeight: 'var(--font-weight-semibold)',
              color: 'var(--text-primary)',
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {draft.name.trim() || 'Untitled schema'}
          </span>
          {isNew ? (
            <span className="chip gray">Draft — not saved</span>
          ) : (
            <span className="chip gray" title={VERSION_HINT}>{`v${base?.version}`}</span>
          )}
          {/* Lifecycle lives in Settings (doc-108) — the state chip is the
              route there (the errors-chip precedent below). */}
          <button
            type="button"
            className={draft.active ? 'chip green' : 'chip gray'}
            style={{ border: 0, cursor: 'pointer', fontFamily: 'inherit' }}
            onClick={() => setTab('settings')}
            title={STATE_HINT}
          >
            {draft.active ? 'Active' : 'Archived'}
          </button>
          {errorCount > 0 && (
            <button
              type="button"
              className="chip red"
              style={{ border: 0, cursor: 'pointer', fontFamily: 'inherit' }}
              onClick={() => setTab('definition')}
              title="Show the error list in the Definition tab"
            >
              {errorCount} error{errorCount === 1 ? '' : 's'}
            </button>
          )}
          {isDirty && <span className="chip gray">Unsaved edits</span>}
          <div style={{ flex: 1 }} />
          <SaveResetActions
            dirty={isDirty}
            saving={saving}
            onSave={onSave}
            onReset={onReset}
            saveDisabledReason={saveDisabledReason}
          />
        </div>

        <div className="muted" style={{ fontSize: 12, marginTop: 6 }}>
          {metaFacts.map((f, i) => (
            <React.Fragment key={i}>
              {i > 0 && ' · '}
              <span title={f.title}>{f.text}</span>
            </React.Fragment>
          ))}
        </div>

        <div className="tabs" role="tablist" aria-label="Schema sections" style={{ marginTop: 10 }}>
          {TABS.map((t) => {
            const Icon = t.icon;
            return (
              <button
                key={t.key}
                type="button"
                role="tab"
                aria-selected={tab === t.key}
                className={tab === t.key ? 'active' : ''}
                onClick={() => setTab(t.key)}
              >
                <Icon className="w-4 h-4" />
                {t.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* ---- Tab: Definition — the edit/validate loop (§3.4) --------------- */}
      <div role="tabpanel" aria-label="Definition" hidden={tab !== 'definition'} style={PANEL_PADDING}>
        <div className="stack-md">
          <div className="row" style={{ gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            {/* One representation at a time (doc-108): the JSON is the schema;
                Preview is its readable projection, on demand at full width. */}
            <div className="segmented" aria-label="Definition view">
              <button
                type="button"
                className={view === 'json' ? 'active' : ''}
                aria-pressed={view === 'json'}
                onClick={() => setView('json')}
              >
                JSON
              </button>
              <button
                type="button"
                className={view === 'preview' ? 'active' : ''}
                aria-pressed={view === 'preview'}
                onClick={() => setView('preview')}
              >
                Preview
              </button>
            </div>
            <div style={{ flex: 1 }} />
            {/* doc-256: the group WRAPS, and must not carry flex-shrink: 0.
                The outer row already wraps, so the group drops to its own
                line — but pinned at max-content it then overflowed that line
                and no ancestor scrolls (the card is overflow: clip), so
                Draft with AI / Copy / Download were simply unreachable once
                the card fell under ~500px. Wrapping is what buys the reach;
                .btn is white-space: nowrap, so each button still keeps its
                full width and never squishes. */}
            <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
              {activeFindings.length > 0 && (
                <button
                  type="button"
                  className="btn outline sm"
                  onClick={requestFix}
                  disabled={fixBusy}
                  aria-busy={fixBusy}
                  title="Ask the AI for a minimal correction of the flagged findings — shown as a diff before anything is applied."
                >
                  {fixBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />}
                  {fixBusy ? 'Fixing…' : 'Fix with AI'}
                </button>
              )}
              <button
                type="button"
                className="btn outline sm"
                onClick={() => setAiOpen(!aiOpen)}
                aria-expanded={aiOpen}
              >
                <Sparkles className="w-3.5 h-3.5" />
                Draft with AI
              </button>
              <button type="button" className="btn outline sm" onClick={copyJson}>
                {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                {copied ? 'Copied' : 'Copy'}
              </button>
              <button type="button" className="btn outline sm" onClick={downloadJson}>
                <Download className="w-3.5 h-3.5" />
                Download
              </button>
            </div>
          </div>

          {aiOpen && !pendingAiDraft && (
            <DraftWithAiPane onDraft={onAiDraft} onCancel={() => setAiOpen(false)} />
          )}
          {pendingAiDraft && (
            <div className="pb-item">
              <div style={{ fontSize: 13, marginBottom: 8 }}>
                Replace the current definition? Your existing JSON will be overwritten.
              </div>
              <div className="row" style={{ gap: 8 }}>
                <button type="button" className="btn primary sm" onClick={() => applyAiDraft(pendingAiDraft)}>
                  Replace
                </button>
                <button type="button" className="btn ghost sm" onClick={() => setPendingAiDraft(null)}>
                  Keep editing
                </button>
              </div>
            </div>
          )}

          <div style={{ minWidth: 0 }}>
            {/* Both views stay MOUNTED (hidden attr) — CM keeps its undo
                history and cursor, the preview keeps its scroll position.
                Height-bounded (doc-105): each scrolls inside the clamp so the
                tab fits one viewport with any size of definition. */}
            <div hidden={view !== 'json'}>
              <div className="sde-pane-scroll">
                <SchemaJsonEditor
                  ref={editorRef}
                  value={jsonText}
                  onChange={(text) => { setJsonText(text); setAiNotice(false); }}
                  ariaLabel="Schema definition JSON"
                />
              </div>
            </div>
            <div hidden={view !== 'preview'}>
              <div className="muted" style={{ fontSize: 12, lineHeight: 1.5, marginBottom: 6 }}>
                The fields the AI is asked to fill — labels, types, and per-field instructions.
                Outputs appear in this shape on a contract's Structured Data tab.
              </div>
              <div className="sde-pane-scroll">
                <SchemaPreview definition={parseError ? draftToCanonicalJson(draft) : parsed} dimmed={!!parseError} />
              </div>
            </div>

            <div style={{ minWidth: 0 }}>
              {/* Status row — below the pane, visible in BOTH views */}
              <div role="status" aria-live="polite" className="stack-sm" style={{ marginTop: 8 }}>
                {errorCount === 0 ? (
                  <div className="row" style={{ gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                    <span className="chip green">{aiNotice ? 'AI draft loaded' : 'Valid'}</span>
                    <span className="muted" style={{ fontSize: 12 }}>
                      {aiNotice
                        ? 'Review the definition, then test it against a contract before saving.'
                        : `${fieldCount} field${fieldCount === 1 ? '' : 's'} · ${itemFieldCount} with item fields`}
                    </span>
                  </div>
                ) : (
                  <div className="stack-sm">
                    <div className="row" style={{ gap: 8, alignItems: 'center' }}>
                      <span className="chip red">{errorCount} error{errorCount === 1 ? '' : 's'}</span>
                      {parseError && <span className="muted" style={{ fontSize: 12 }}>The JSON doesn't parse.</span>}
                    </div>
                    <div className="stack-sm" style={{ gap: 2 }}>
                      {(parseError ? [{ pos: parseError.pos, message: parseError.message, label: '' }] : errors).map((e, i) => (
                        <button
                          key={i}
                          type="button"
                          onClick={() => jumpToIssue(e)}
                          className="row"
                          style={{
                            gap: 8, background: 'none', border: 0, padding: '2px 0', cursor: 'pointer',
                            textAlign: 'left', fontFamily: 'inherit', fontSize: 12.5, color: 'var(--text-primary)',
                            alignItems: 'baseline',
                          }}
                          title="Jump to the marker"
                        >
                          <span className="mono" style={{ fontSize: 11.5, color: 'var(--text-critical)', flexShrink: 0 }}>
                            {e.label || (typeof e.pos === 'number' ? `pos ${e.pos}` : '')}
                          </span>
                          <span style={{ minWidth: 0 }}>{e.message}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Will this schema actually run constrained? (doc-320) Advisory
                  only — never blocks Save. Hidden while there are errors: the
                  definition can't run at all until those are fixed. */}
              {constrained && errorCount === 0 && (constrained.constrained ? (
                <div className="row" style={{ gap: 6, alignItems: 'center', marginTop: 6 }}>
                  <ShieldCheck className="w-3 h-3" style={{ flexShrink: 0, color: 'var(--text-green)' }} />
                  <span className="muted" style={{ fontSize: 12, minWidth: 0 }}>
                    Structured outputs on — runs are constrained to this exact shape
                    ({constrained.unionCount} of {constrained.max} counted fields; Choice fields don't count).
                  </span>
                </div>
              ) : (
                <div className="callout yellow" style={{ marginTop: 8 }}>
                  <div className="callout-icon"><AlertTriangle className="w-4 h-4" /></div>
                  <div className="callout-body" style={{ fontSize: 12.5 }}>
                    <strong>Structured outputs off for this schema.</strong>{' '}
                    {constrained.reason === 'union_limit' ? (
                      <>
                        {constrained.unionCount} of its fields count toward the provider's cap of{' '}
                        {constrained.max}, so every run falls back to the older prompt-only path — no
                        guaranteed output shape, and measurably more tokens and latency. Two ways back
                        under the cap: split this into two schemas, or turn fields into Choice fields
                        (a Choice field with options doesn't count).
                      </>
                    ) : (
                      <>
                        {constrained.fields.length === 1 ? 'List/object field ' : 'List/object fields '}
                        {listKeys(constrained.fields)}{' '}
                        {constrained.fields.length === 1 ? 'has' : 'have'} no item fields, so the output
                        shape can't be pinned down and every run falls back to the older prompt-only
                        path. Declare item fields to switch it back on.
                      </>
                    )}{' '}
                    Saving is unaffected.
                  </div>
                </div>
              ))}

              {fixError && (
                <div className="callout red" style={{ marginTop: 8 }}>
                  <div className="callout-icon"><AlertCircle className="w-4 h-4" /></div>
                  <div className="callout-body" style={{ fontSize: 12.5 }}>AI fix failed: {fixError}</div>
                </div>
              )}
              {pendingFix && pendingFix.requestText === jsonText && (
                <div style={{ marginTop: 8 }}>
                  <AiFixPane
                    currentText={jsonText}
                    fixedText={pendingFix.fixedText}
                    onApply={() => {
                      setJsonText(pendingFix.fixedText);
                      setPendingFix(null);
                      setAiNotice(false);
                    }}
                    onDiscard={() => setPendingFix(null)}
                  />
                </div>
              )}

              {/* Semantic warnings — advisory, never block Save */}
              {warnings.length > 0 && (
                <div className="callout yellow" style={{ marginTop: 8 }}>
                  <div className="callout-icon"><AlertTriangle className="w-4 h-4" /></div>
                  <div className="callout-body stack-sm" style={{ fontSize: 12.5, gap: 6 }}>
                    {backfillError && (
                      <div style={{ color: 'var(--text-critical)' }}>Item-field generation failed: {backfillError}</div>
                    )}
                    {warnings.map((w, i) => (
                      <div key={i} className="row" style={{ gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                        <span style={{ minWidth: 0 }}>{w.message}</span>
                        {w.kind === 'no-item-fields' && (
                          <button
                            type="button"
                            className="btn outline xs"
                            onClick={() => backfillItemFields(w)}
                            disabled={backfillBusyKey !== null}
                            aria-busy={backfillBusyKey === (w.fieldKey || String(w.fieldIndex))}
                            // .btn is white-space: nowrap, so the label IS the button's
                            // min-content width — a long one escapes the callout at a
                            // narrow content column. Short visible text, full accessible
                            // name. The warning beside it already says "item fields".
                            aria-label="Generate item fields with AI"
                          >
                            {backfillBusyKey === (w.fieldKey || String(w.fieldIndex))
                              ? <Loader2 className="w-3 h-3 animate-spin" />
                              : <Sparkles className="w-3 h-3" />}
                            Generate with AI
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ---- Tab: Settings — identity, targeting, prompt (§3.5) ------------ */}
      <div role="tabpanel" aria-label="Settings" hidden={tab !== 'settings'} style={PANEL_PADDING}>
        <div className="stack-lg">
          <div className="muted" style={{ fontSize: 12, lineHeight: 1.5 }}>{VERSION_HINT}</div>

          <FormSection title="Name & description">
            {scalarsDisabled && (
              <div className="muted" style={{ fontSize: 12 }}>Fix the JSON to edit these fields.</div>
            )}
            <div>
              <div style={META_LABEL}>Name</div>
              <input
                className="input"
                value={draft.name}
                onChange={(e) => onPatch({ name: e.target.value })}
                placeholder="e.g. Payment terms"
                disabled={scalarsDisabled}
              />
            </div>
            <div>
              <div style={META_LABEL}>Description</div>
              <input
                className="input"
                value={draft.description}
                onChange={(e) => onPatch({ description: e.target.value })}
                placeholder="What this schema is for — shown on the contract tab and to API consumers."
                disabled={scalarsDisabled}
              />
            </div>
          </FormSection>

          <FormSection
            title="Applies to"
            hint="Which contracts this schema targets. Leave the type and category lists unchecked to match every type or category. The status settings work the other way round — they are off by default, and off means those contracts are excluded."
          >
            <div className="muted" style={{ fontSize: 12 }}>{appliesToSummary}</div>
            <div className="stack-md">
              <TargetChecklist
                title="Contract types"
                options={typeOptions}
                value={draft.targetTypes}
                onChange={(v) => onPatch({ targetTypes: v })}
                disabled={scalarsDisabled}
              />
              {categoryOptions.length > 0 && (
                <TargetChecklist
                  title="Categories"
                  options={categoryOptions}
                  value={draft.targetCategories}
                  onChange={(v) => onPatch({ targetCategories: v })}
                  disabled={scalarsDisabled}
                />
              )}
              <div>
                <div style={META_LABEL}>Contract status</div>
                <label className="pb-check">
                  <input
                    type="checkbox"
                    checked={!!draft.includeDrafts}
                    onChange={(e) => onPatch({ includeDrafts: e.target.checked })}
                    disabled={scalarsDisabled}
                  />
                  <span>Include draft contracts</span>
                </label>
                <label className="pb-check">
                  <input
                    type="checkbox"
                    checked={!!draft.includeTerminated}
                    onChange={(e) => onPatch({ includeTerminated: e.target.checked })}
                    disabled={scalarsDisabled}
                  />
                  <span>Include terminated contracts</span>
                </label>
                <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
                  Drafts are unexecuted, so extracting from them produces values that look real but
                  were never agreed. Turning either off removes those contracts from the matching
                  count, batch runs, and the API — any values already extracted for them stay
                  readable on the contract, marked as no longer applicable. Same as the
                  "includeDrafts" / "includeTerminated" keys in the JSON definition.
                </div>
              </div>
            </div>
          </FormSection>

          <FormSection title="Extraction prompt">
            {scalarsDisabled && (
              <div className="muted" style={{ fontSize: 12 }}>Fix the JSON to edit these fields.</div>
            )}
            <div>
              <div style={META_LABEL}>Model</div>
              <select
                className="select"
                value={draft.model || (models[0]?.value || '')}
                onChange={(e) => onPatch({ model: e.target.value })}
                style={{ maxWidth: 480 }}
                disabled={scalarsDisabled}
              >
                {/* A schema pinned to a model that has since left the server's
                    options list gets its own option, so the <select> renders
                    its real value instead of blank — a blank select silently
                    re-pins the schema to the default on the next interaction. */}
                {legacyModel && (
                  <option value={legacyModel}>{`${legacyModel} — legacy`}</option>
                )}
                {models.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
              </select>
              <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>The default is right for most schemas.</div>
            </div>
            <div>
              <div style={META_LABEL}>Prompt preamble (optional)</div>
              <textarea
                className="textarea"
                value={draft.promptPreamble}
                onChange={(e) => onPatch({ promptPreamble: e.target.value })}
                placeholder="Context the AI reads before the field list — e.g. 'Amounts are in the contract's governing currency; dates in ISO format. For pricing tiers, an open-ended tier like ‘Above $10,000,000’ has min = prior tier's upper bound + 1, not the bound itself.'"
                style={{ width: '100%', minHeight: 120, resize: 'vertical', lineHeight: 1.55 }}
                disabled={scalarsDisabled}
              />
              <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
                Each field's description is its own per-field prompt; this preamble sets shared context.
                Recommended convention for tiered/banded values: state the boundary semantics explicitly
                (e.g. "open-ended tiers: min = the prior tier's upper bound + 1"), so "Above $X" isn't extracted as min = X.
              </div>
            </div>
          </FormSection>

          <FormSection
            title="Extraction sources"
            hint="Which documents a run reads beyond the contract's own files. Reference documents (pricing sheets, order forms) are always included."
          >
            {scalarsDisabled && (
              <div className="muted" style={{ fontSize: 12 }}>Fix the JSON to edit this setting.</div>
            )}
            <label className="pb-check">
              <input
                type="checkbox"
                checked={!!draft.includeRelatedSources}
                onChange={(e) => onPatch({ includeRelatedSources: e.target.checked })}
                disabled={scalarsDisabled}
              />
              <span>Include related contracts' documents as extraction sources</span>
            </label>
            <div className="muted" style={{ fontSize: 12 }}>
              Pulls in documents from related contracts (amendments, side letters) by default —
              the run dialog can still override this per run. Reference documents are always read,
              with or without this setting. Same as the "includeRelatedSources" key in the JSON definition.
            </div>
          </FormSection>

          {/* Lifecycle lives here (doc-108) — rail rows are pure navigation,
              so archive/restore/delete act where the state is explained.
              Drafts have nothing to archive. */}
          {!isNew && base && (
            <FormSection
              title="Archive & delete"
              hint={draft.active
                ? 'Archiving hides this schema from contract pages and blocks runs — batch runs skip it. Extracted outputs stay readable, labeled "Schema off", and you can restore it anytime.'
                : 'This schema is archived — hidden from contract pages, and runs are blocked. Its outputs stay readable on contracts, labeled "Schema off".'}
            >
              {draft.active ? (
                <div className="row" style={{ gap: 8 }}>
                  <button type="button" className="btn outline sm" onClick={onArchive}>
                    <Archive className="w-3.5 h-3.5" />
                    Archive schema
                  </button>
                </div>
              ) : (
                <div className="row" style={{ gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                  <button type="button" className="btn outline sm" onClick={onRestore}>
                    <ArchiveRestore className="w-3.5 h-3.5" />
                    Restore schema
                  </button>
                  {typeof outputCount === 'number' && outputCount > 0 ? (
                    /* Never a disabled danger button — it reads as fully
                       enabled in dark mode. Explain the block instead. */
                    <div className="muted" style={{ fontSize: 12 }}>
                      Can't delete — {outputCount} extracted output{outputCount === 1 ? '' : 's'} reference{outputCount === 1 ? 's' : ''} this
                      schema. Deleting would orphan {outputCount === 1 ? 'it' : 'them'}.
                    </div>
                  ) : (
                    <button type="button" className="btn danger sm" onClick={onDelete}>
                      <Trash2 className="w-3.5 h-3.5" />
                      Delete permanently
                    </button>
                  )}
                </div>
              )}
            </FormSection>
          )}
        </div>
      </div>

      {/* ---- Tab: Test & run — draft dry-run, then saved-version ops (§3.6) - */}
      <div role="tabpanel" aria-label="Test and run" hidden={tab !== 'test'} style={PANEL_PADDING}>
        <div className="stack-lg">
          {/* The authoring loop's exit gate — dry-runs the DRAFT. */}
          <TestRunPane draft={draft} />

          {isNew || !base ? (
            <div className="muted" style={{ fontSize: 12.5 }}>
              Save the schema first — batch runs and exports act on the saved version.
            </div>
          ) : (
            <>
              {/* Keyed by schema id: this editor is NOT remounted when only the
                  saved base changes; without the key the pane's confirm/progress
                  state could leak across schemas (the doc-49 class of bug). */}
              <SchemaAutomationPanel schema={base} canManage={canManageAutomation} onSave={onSaveAutomation} />
              {canRunBatches && <BatchRunPane
                key={base.id}
                schema={base}
                isDirty={isDirty}
                preview={batchPreview}
                previewError={batchPreviewError}
                onRefreshPreview={() => setBatchPreview(null)}
              />}
              {!canRunBatches && <p className="muted" style={{ fontSize: 12 }}>Ask an admin for the Run extractions permission to process contracts.</p>}

              {canExport && <FormSection
                title="Dataset export"
                hint="Download this schema's extracted outputs across all contracts. The same data is available to services via the REST API (Admin settings → API keys)."
              >
                <div className="stack-sm">
                  {exportError && (
                    <div className="callout red">
                      <div className="callout-icon"><AlertCircle className="w-4 h-4" /></div>
                      <div className="callout-body" style={{ fontSize: 12.5 }}>{exportError}</div>
                    </div>
                  )}
                  <label className="pb-check">
                    <input
                      type="checkbox"
                      checked={exportVerifiedOnly}
                      onChange={(e) => setExportVerifiedOnly(e.target.checked)}
                    />
                    <span>Verified outputs only</span>
                  </label>
                  <div className="row" style={{ gap: 8 }}>
                    {['csv', 'json'].map((format) => (
                      <button
                        key={format}
                        type="button"
                        className="btn outline sm"
                        disabled={!!exporting}
                        onClick={async () => {
                          setExporting(format);
                          setExportError('');
                          try {
                            await downloadSchemaExport(base.id, {
                              format,
                              verification: exportVerifiedOnly ? 'verified' : '',
                            });
                          } catch (err) {
                            setExportError(err.message);
                          } finally {
                            setExporting('');
                          }
                        }}
                      >
                        <Download className="w-3.5 h-3.5" />
                        {exporting === format ? 'Exporting…' : `Download ${format.toUpperCase()}`}
                      </button>
                    ))}
                  </div>
                </div>
              </FormSection>}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
